import React, { useCallback } from 'react';
import { Modal } from 'antd';
import { ButtonColors, StyledButtonClassName } from '../form-control/styledButton/styledButton';
import styles from './alert.module.css';

interface AlertOptions {
	title: string | React.ReactNode;
	iconColor?: 'info' | 'warning' | 'danger' | 'success';
	icon?: React.JSX.Element;
	content: string | React.ReactNode;
	okText: string;
	cancelText?: string;
	okButtonColor?: ButtonColors;
	cancelButtonColor?: ButtonColors;
	onOk?: ()=> void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onCancel?: (...args: any[]) => any;
  afterClose?: () => void;
}


export interface UseAlertInstance {
	/** alert with yes/no buttons */
	confirm: (options: AlertOptions) => { destroy: () => void; update: (configUpdate: any) => void };
	info: (options: AlertOptions) => { destroy: () => void; update: (configUpdate: any) => void };
	warn: (options: AlertOptions) => { destroy: () => void; update: (configUpdate: any) => void };
	success: (options: AlertOptions) => { destroy: () => void; update: (configUpdate: any) => void };
	error: (options: AlertOptions) => { destroy: () => void; update: (configUpdate: any) => void };
}

const getStandardProps = (options: AlertOptions) => {
	return {
		...options,
		cancelButtonProps: {
			className: StyledButtonClassName,
			'btn-type': options.cancelButtonColor || 'light'
		},

		okButtonProps: {
			className: StyledButtonClassName,
			'btn-type': options.okButtonColor || 'primary'
		}
	};
};

/**
 * Wraps a modal instance's update method to properly merge settings and button props
 * @param instance - The modal instance returned from Modal methods
 * @param settings - The original settings object used to create the modal
 * @returns The instance with wrapped update method
 */
const wrapModalInstance = (instance: { destroy: () => void; update: (configUpdate: any) => void }, settings: any) => {
	const originalUpdate = instance.update;
	instance.update = (configUpdate: any) => {
		const mergedUpdate: any = {
			...settings,
			...configUpdate,
			okButtonProps: { ...settings.okButtonProps, ...configUpdate.okButtonProps }
		};
		
		// Only merge cancelButtonProps if they exist in settings
		if (settings.cancelButtonProps) {
			mergedUpdate.cancelButtonProps = { ...settings.cancelButtonProps, ...configUpdate.cancelButtonProps };
		}
		
		originalUpdate(mergedUpdate);
	};
	return instance;
};

const useAlert = (): UseAlertInstance => {
	const confirm = useCallback((options: AlertOptions) => {
		const settings = getStandardProps(options);
		if (options.iconColor) {
			delete options.icon;
			// @ts-expect-error className is a custom property from Modal.confirm
			settings.className = `${styles.Wrapper} ${options.iconColor}`;
		}

		const instance = Modal.confirm(settings);
		return wrapModalInstance(instance, settings);
	}, []);

	const info = useCallback((options: AlertOptions) => {
		const settings = getStandardProps(options);
		const instance = Modal.info(settings);
		return wrapModalInstance(instance, settings);
	}, []);

	const error = useCallback((options: AlertOptions) => {
		const settings = getStandardProps({ okButtonColor: 'danger', ...options });
		const instance = Modal.error(settings);
		return wrapModalInstance(instance, settings);
	}, []);

	const warn = useCallback((options: AlertOptions) => {
		const settings = getStandardProps({ okButtonColor: 'warning', ...options });
		const instance = Modal.warning(settings);
		return wrapModalInstance(instance, settings);
	}, []);

	const success = useCallback((options: AlertOptions) => {
		const settings = getStandardProps({ okButtonColor: 'primary', ...options });
		const instance = Modal.success(settings);
		return wrapModalInstance(instance, settings);
	}, []);

	return {
		confirm,
		info,
		error,
		warn,
		success
	};
};

export default useAlert;
